/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  MultipleListsSharedView,
  getListBySlugThunk,
  Loader,
} from '@resy/ui-kitchen';
import {
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
} from '../../../../../ui-kitchen/src/reducers';
import { MODAL_MODES } from '../../../../../ui-kitchen/src/reducers/favoriteSlice';
import {
  IDLE, PENDING,
} from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { getImageUrl } from '../../../../../ui-kitchen/src/utils';
import transformListCreatedBy from '../../../../../ui-kitchen/src/transformers/transformListCreatedBy';
import { deleteFollowListBySlug, setFollowListBySlug } from '../../../../../ui-kitchen/src/api/api';

import './MultipleListsSharedContainer.scss';

const VENUE_LIMIT = 15;

const MultipleListsSharedContainer = ({
  $rootScope, $routeParams, Analytics, list, isAuthenticated, isMobile, getListBySlugThunk, setMultiStepModalMode, setMultiStepModalOpenState, setMultiStepModalVenueId, setMultiStepModalVenueContext,
}) => {
  const [offset, setOffset] = useState(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(undefined);

  const setList = () => {
    const results = list?.results;
    setOffset(results?.metadata?.offset);
  };

  const handleMoreVenues = async () => {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 300) {
      const results = list?.results;
      if (results === undefined) return;

      const { venues } = results?.results;

      if (!isLoading && venues.length < results.list.count) {
        setIsLoading(true);
        await getListBySlugThunk({
          slug: $routeParams.guid,
          limit: VENUE_LIMIT,
          offset: offset + parseInt(VENUE_LIMIT, 10),
        });
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    Analytics.event('view list', {
      'shared list': true,
    });

    const fetchList = async () => {
      try {
        if (list?.status !== PENDING) {
          await getListBySlugThunk({
            slug: $routeParams?.guid,
            limit: VENUE_LIMIT,
            offset: 0,
          });
        }
      } catch (err) {
        $rootScope.prerender.responseCode = '404';
        Logger.error('[MultipleListsSharedContainer] Error: getListBySlugThunk()', err);
      }
    };

    fetchList();
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleMoreVenues);
    return () => {
      window.removeEventListener('scroll', handleMoreVenues);
    };
  }, [handleMoreVenues]);

  useEffect(() => {
    const results = list?.results;
    const newOffset = results?.metadata?.offset;

    if (results && offset !== newOffset) {
      setList();
    }
  }, [list?.results]);

  const handleFavoriteClick = async ({ venue_id: venueId, venue }) => {
    const showMultiListModal = () => {
      setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.locality });
      setMultiStepModalVenueId(venueId);
      setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST);
      setMultiStepModalOpenState(true);
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      $rootScope.$broadcast('MODAL', 'signin', { successCallback: showMultiListModal });
    }
  };

  const handleCopyListClick = async () => {
    const showMultiListModal = () => {
      setMultiStepModalMode(MODAL_MODES.COPY_LIST);
      setMultiStepModalOpenState(true);
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      $rootScope.$broadcast('MODAL', 'signin', { successCallback: showMultiListModal });
    }
  };

  const handleFollowListClick = async (follow) => {
    const { guid: urlSlug } = $routeParams;

    const toggleFollowing = async () => {
      if (urlSlug) {
        if (follow) {
          try {
            await setFollowListBySlug(urlSlug);
            setIsFollowing(true);
            Analytics.event('follow list');
          } catch (err) {
            if (err?.status === 400) { // the list has already been followed
              setIsFollowing(true);
            }
            Logger.error('[MultipleListsSharedContainer] Follow List Error:', err);
          }
        } else {
          try {
            await deleteFollowListBySlug(urlSlug);
            setIsFollowing(false);
          } catch (err) {
            if (err?.status === 400) { // the list is already been removed
              setIsFollowing(false);
            }
            Logger.error('[MultipleListsSharedContainer] Remove Following Error:', err);
          }
        }
      }
    };

    if (!isAuthenticated) {
      $rootScope.$broadcast('MODAL', 'signin', { successCallback: toggleFollowing });
    } else {
      toggleFollowing();
    }
  };

  const results = list?.results;

  if (results === undefined) {
    return (
      <MultipleListsSharedView
        isLoading
      />
    );
  }

  if (results.results?.venues === null) {
    return (
      <MultipleListsSharedView
        isError
        listId={$routeParams?.guid}
      />
    );
  }

  if (isFollowing === undefined && results?.is_followed) {
    setIsFollowing(results.is_followed);
  }

  const thumbnail = (results?.list?.responsive_images) ? getImageUrl(results?.list?.responsive_images) : undefined;
  setTimeout(() => {
    $rootScope.metadata.title = `View this list on Resy: ${results?.list?.name}`;
    $rootScope.metadata.image = thumbnail;
    $rootScope.metadata.image_facebook = thumbnail;
    $rootScope.metadata.image_twitter = thumbnail;
  }, 100);

  return (
    <div className="MultipleListsSharedContainer">
      <MultipleListsSharedView
        onFavoriteClick={handleFavoriteClick}
        onFollowListClick={handleFollowListClick}
        onCopyListClick={handleCopyListClick}
        name={results?.list?.name}
        description={results?.list?.description}
        createdBy={transformListCreatedBy(results?.created_by)}
        count={results?.list?.count}
        listId={results?.list?.list_id}
        isLegacyHitlist={results?.list?.is_legacy_hitlist}
        venues={results?.results?.venues}
        isMobile={isMobile}
        isFollowing={isFollowing}
        event={Analytics.event}
        urlSlug={results?.list?.url_slug}
      />
      {isLoading && (<Loader containerHeight="100px" />)}
    </div>
  );
};

MultipleListsSharedContainer.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    metadata: PropTypes.shape({
      image: PropTypes.string,
      image_facebook: PropTypes.string,
      image_twitter: PropTypes.string,
      title: PropTypes.string,
    }),
    prerender: PropTypes.shape({
      responseCode: PropTypes.string,
    }),
  }).isRequired,
  $routeParams: PropTypes.shape({
    guid: PropTypes.string,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  list: PropTypes.shape({
    status: PropTypes.string,
    results: PropTypes.shape({
      created_by: PropTypes.shape({}),
      is_followed: PropTypes.bool,
      list: PropTypes.shape({
        name: PropTypes.string,
        description: PropTypes.string,
        count: PropTypes.number,
        list_id: PropTypes.string,
        is_legacy_hitlist: PropTypes.bool,
        responsive_images: PropTypes.shape({}),
        url_slug: PropTypes.string,
      }),
      metadata: PropTypes.shape({
        offset: PropTypes.number,
      }),
      results: PropTypes.shape({
        venues: PropTypes.shape([]),
      }),
    }),
  }),
  isAuthenticated: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  getListBySlugThunk: PropTypes.func.isRequired,
  setMultiStepModalMode: PropTypes.func.isRequired,
  setMultiStepModalOpenState: PropTypes.func.isRequired,
  setMultiStepModalVenueId: PropTypes.func.isRequired,
  setMultiStepModalVenueContext: PropTypes.func.isRequired,
};

MultipleListsSharedContainer.defaultProps = {
  list: {
    status: IDLE,
    results: undefined,
  },
  isMobile: false,
};

const mapStateToProps = (state) => {
  const { matchMedia: { md }, favorite } = state;
  return ({
    isAuthenticated: selectIsAuthenticated(state),
    isMobile: !md,
    list: favorite.listByShareSlug,
  });
};

const mapDispatchToProps = {
  getListBySlugThunk,
  setMultiStepModalMode,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
};

MultipleListsSharedContainer.displayName = 'MultipleListsSharedContainer';
export { mapStateToProps, MultipleListsSharedContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(MultipleListsSharedContainer);
